import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Chip,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import { formatCurrency, formatDate } from '../../../../utils/utils';
import { fetchDocuments } from '../../../../utils/FetchDocuments';
import { useToast } from '../../../../components/Toast';
import { Loading } from '../../../../components/Loading';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface ServiceLogViewModalProps {
    open: boolean;
    onClose: () => void;
    log: any;  // Using any since we're getting direct API response
}

const ServiceLogViewModal: React.FC<ServiceLogViewModalProps> = ({ open, onClose, log }) => {
    const { showToast } = useToast();

    const [loading, setLoading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

    const fetchServiceDocuments = async (serviceLogId: string) => {
        try {
            setLoading(true);
            const documents = await fetchDocuments({
                documentContext: "service-log",
                documentTypeId: serviceLogId,
                documentType: "service-documents"
            });

            setUploadedFiles(documents.map((doc: any) => ({
                id: doc.id,
                fileName: doc.fileName,
                presignedUrl: doc.presignedUrl
            })));
        } catch (error) {
            console.error("Error fetching service documents:", error);
            showToast("Failed to fetch service documents", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && log) {
            fetchServiceDocuments(log?.id);
        }
    }, [open, log?.id]);

    if (!log) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ fontSize: '1.1rem', pb: 1 }}>
                Service Log Details
            </DialogTitle>
            <DialogContent>
                {
                    loading ? <Loading /> : (
                        <Box sx={{ mt: 1 }}>
                            {/* Basic Info */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                        Service Date
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.875rem' }}>
                                        {formatDate(log.serviceDate)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                        Service Type
                                    </Typography>
                                    <Chip
                                        label={log.serviceType}
                                        color={log.serviceType === 'FREE' ? 'success' : 'default'}
                                        size="small"
                                        sx={{ fontSize: '0.75rem', height: '20px' }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                        Garage
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.875rem' }}>
                                        {log.garageDetails.name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                        Odometer Reading
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.875rem' }}>
                                        {log.odometer} km
                                    </Typography>
                                </Grid>
                                {log.durationSinceLastService && (
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                            Duration Since Last Service
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.875rem' }}>
                                            {log.durationSinceLastService} months
                                        </Typography>
                                    </Grid>
                                )}
                                {log.remarks && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                            Remarks
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.875rem' }}>
                                            {log.remarks}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>

                            {/* Expense Table */}
                            {log.expenses && log.expenses.length > 0 && (
                                <>
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontSize: '0.9rem' }}>
                                        Service Expenses
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>Item</TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>Category</TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>Action</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>Quantity</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>Rate</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>Amount</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>GST</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {log.expenses.map((expense: any, index: number) => (
                                                    <TableRow key={index}>
                                                        <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{expense.item}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{expense.category}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{expense.action}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>{expense.quantity}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>{formatCurrency(parseFloat(expense.rate))}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>{formatCurrency(parseFloat(expense.amount))}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>{expense.gst}%</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {
                                uploadedFiles.length > 0 && (
                                    <>
                                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontSize: '0.9rem' }}>
                                            Service Documents
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {
                                                uploadedFiles.map((file) => (
                                                    <Grid item xs={12} sm={2} key={file.id}>
                                                        <img
                                                            src={file.presignedUrl}
                                                            alt={file.fileName}
                                                            onClick={() => window.open(file.presignedUrl, '_blank')}
                                                            width={80}
                                                            height={60}
                                                            style={{ objectFit: "cover", borderRadius: 4, cursor: 'pointer', border: '1px solid #ccc' }}
                                                        />
                                                    </Grid>
                                                ))
                                            }
                                        </Grid>
                                    </>
                                )
                            }

                            {/* Summary */}
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                            Total Amount
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                            {formatCurrency(parseFloat(log.amount))}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                            GST
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                            {formatCurrency(parseFloat(log.gst))}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                            Total (Inc. GST)
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                            {formatCurrency(parseFloat(log.totalAmount))}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    size="small"
                    sx={{ fontSize: '0.8rem' }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ServiceLogViewModal; 